import { useEffect, useRef } from 'react';
import { track } from '@/analytics';
import { GUIDE_LINKS, pagePath } from '@/site/siteNav';
import { promptInstall, useInstallOffer } from '@/pwa/installPrompt';
import { Button } from '@/components/ui/primitives/Button';
import './SiteHeader.css';

/**
 * The site top bar (design folder _design-topbar, Option B; decision D5 the icon).
 * One row at every width, sticky, a fixed 57 px: the logo (icon + serif wordmark in the spot colour) left; How it
 * works, Guides (a native <details> listing every registry guide page as a real <a href>, so the links are in the
 * prerendered HTML) and What's new; right: Open a PDF -- free (the primary action) and a quiet Sign in (accounts are
 * invite-only: no sign-up). On a phone the row keeps the logo + Open a PDF + a Menu (<details>) that holds the rest. The EN / DE / FR switch shows on the landing
 * pages only (the guide pages are English only).
 *
 * Plain <a href> everywhere (no router <Link>): the bar renders the same in a prerendered page, the SPA, /login and
 * Storybook. The CTAs (Open a PDF, Sign in) report `site_cta_clicked` { cta, placement } on click,
 * fire-and-forget (the link navigates as usual). Kit classes (.bk-logo, .bk-nav, .bk-button); the bar's own layout + the menu panels are
 * SiteHeader.css (the kit has no menu class yet).
 */
export interface SiteHeaderContent {
    /** The accessible name of the bar's nav landmark. */
    navLabel: string;
    howItWorks: string;
    guides: string;
    whatsNew: string;
    /** The primary action: open a PDF, free. */
    openPdf: string;
    signIn: string;
    /** The phone menu button's name. */
    menu: string;
    /** The accessible name of the language switch. */
    languageLabel: string;
    /** The Menu's install entry, shown only when the browser offers an install (Chromium `beforeinstallprompt`). */
    installApp: string;
    /** iOS / iPadOS Safari, which has no install prompt: a one-line hint in the Menu instead (docs/offline.md). */
    installHint: string;
}

export const SITE_HEADER_EN: SiteHeaderContent = {
    navLabel: 'Site',
    howItWorks: 'How it works',
    guides: 'Guides',
    whatsNew: "What's new",
    openPdf: 'Open a PDF — free',
    signIn: 'Sign in',
    menu: 'Menu',
    languageLabel: 'Language',
    installApp: 'Install app',
    installHint: 'Add to Home Screen to keep it offline',
};

export type SiteLocale = 'en' | 'de' | 'fr';

type SiteCta = 'open_pdf' | 'sign_in' | 'install';
/** A CTA click handler: report it and let the link navigate (never preventDefault, never await). */
const ctaClick = (cta: SiteCta, placement: 'bar' | 'menu') => () => track('site_cta_clicked', { cta, placement });

const LOCALES: ReadonlyArray<{ locale: SiteLocale; code: string; name: string; href: string }> = [
    { locale: 'en', code: 'EN', name: 'English', href: pagePath('/') },
    { locale: 'de', code: 'DE', name: 'Deutsch', href: pagePath('/de') },
    { locale: 'fr', code: 'FR', name: 'Français', href: pagePath('/fr') },
];

/** On a landing page (/, /de, /fr) the bar's anchors stay on the page and Open a PDF starts the page's file picker. */
export interface SiteHeaderLanding {
    locale: SiteLocale;
    /** Open the landing's file picker (the existing open-file flow). */
    onOpenPdf?: () => void;
}

export interface SiteHeaderProps {
    content?: SiteHeaderContent;
    landing?: SiteHeaderLanding;
    /** The current page's path (`/whats-new/`, `/login`): its link is marked aria-current="page". */
    currentPath?: string;
}

const LOGIN_PATH = '/login';

export function SiteHeader({ content = SITE_HEADER_EN, landing, currentPath }: SiteHeaderProps) {
    const ref = useRef<HTMLElement>(null);
    useMenusClose(ref);
    const install = useInstallOffer();

    const c = content;
    const home = landing ? (LOCALES.find((l) => l.locale === landing.locale)?.href ?? '/') : '/';
    // On a landing page the section anchors stay in the page; elsewhere they go to the English landing.
    const anchor = (id: string) => (landing ? `#${id}` : `/#${id}`);
    const current = (href: string) => (currentPath === href ? ('page' as const) : undefined);
    const whatsNewHref = pagePath('/whats-new');
    // The anchor still scrolls to the drop zone (#open), where the picked file shows; the picker opens on top.
    const onOpen = () => {
        track('site_cta_clicked', { cta: 'open_pdf', placement: 'bar' });
        landing?.onOpenPdf?.();
    };

    const guideItems = GUIDE_LINKS.map((g) => (
        <li key={g.route}>
            <a href={g.href} aria-current={current(g.href)}>{g.label}</a>
        </li>
    ));

    return (
        <header className="site-header" ref={ref} data-testid="site-header">
            <div className="site-header__row">
                <a className="bk-logo site-header__logo" href={home}>
                    <img src="/icons/icon.svg" alt="" width={32} height={32} />
                    <span className="site-header__wordmark">PDF Markup Tool</span>
                </a>
                <nav className="bk-nav site-header__nav" aria-label={c.navLabel}>
                    <a className="site-header__how" href={anchor('how-it-works')}>{c.howItWorks}</a>
                    <details className="site-header__guides">
                        <summary>{c.guides}</summary>
                        <ul className="site-header__panel">{guideItems}</ul>
                    </details>
                    <a className="site-header__new" href={whatsNewHref} aria-current={current(whatsNewHref)}>{c.whatsNew}</a>
                </nav>
                <div className="site-header__actions">
                    <a className="bk-button bk-button--primary site-header__open" href={anchor('open')} onClick={onOpen}
                        data-testid="site-open-pdf">
                        {c.openPdf}
                    </a>
                    <a className="site-header__signin" href={LOGIN_PATH} aria-current={current(LOGIN_PATH)}
                        onClick={ctaClick('sign_in', 'bar')} data-testid="landing-sign-in">
                        {c.signIn}
                    </a>
                    {landing && <LanguageSwitch label={c.languageLabel} locale={landing.locale} className="site-header__lang" />}
                    <details className="site-header__menu">
                        <summary aria-label={c.menu}>
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </summary>
                        <div className="site-header__sheet">
                            <a href={anchor('how-it-works')}>{c.howItWorks}</a>
                            <p className="bk-label site-header__sheet-label">{c.guides}</p>
                            <ul className="site-header__sheet-list">{guideItems}</ul>
                            <a href={whatsNewHref}>{c.whatsNew}</a>
                            <a href={LOGIN_PATH} onClick={ctaClick('sign_in', 'menu')}>{c.signIn}</a>
                            {install === 'prompt' && (
                                <div className="site-header__install">
                                    <Button variant="ghost" size="touch" data-testid="site-install"
                                        onClick={() => { ctaClick('install', 'menu')(); void promptInstall(); }}>
                                        {c.installApp}
                                    </Button>
                                </div>
                            )}
                            {install === 'ios-hint' && (
                                <p className="site-header__install-hint" data-testid="site-install-hint">{c.installHint}</p>
                            )}
                            {landing && <LanguageSwitch label={c.languageLabel} locale={landing.locale} className="site-header__sheet-lang" />}
                        </div>
                    </details>
                </div>
            </div>
        </header>
    );
}

function LanguageSwitch({ label, locale, className }: { label: string; locale: SiteLocale; className: string }) {
    return (
        <nav className={className} aria-label={label}>
            {LOCALES.map((l) => (
                <a key={l.locale} href={l.href} lang={l.locale} hrefLang={l.locale} aria-label={l.name}
                    aria-current={l.locale === locale ? 'page' : undefined}>
                    {l.code}
                </a>
            ))}
        </nav>
    );
}

/**
 * The bar's two disclosures (Guides, the phone Menu) close on Escape (focus back on their summary), on a click
 * outside them and on picking a link inside them; opening one closes the other. Native <details> does the rest
 * (open / close by click, Enter or Space on the summary), so the links work without JS.
 */
function useMenusClose(ref: React.RefObject<HTMLElement | null>) {
    useEffect(() => {
        const root = ref.current;
        if (!root) return;
        const open = () => Array.from(root.querySelectorAll<HTMLDetailsElement>('details[open]'));
        const onClick = (e: Event) => {
            const target = e.target instanceof Element ? e.target : null;
            for (const d of open()) {
                if (!target || !d.contains(target) || target.closest('a')) d.open = false;
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            for (const d of open()) {
                d.open = false;
                d.querySelector('summary')?.focus();
            }
        };
        document.addEventListener('click', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [ref]);
}
